import { useCallback } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { triggerExtractPending } from './bankingSlice';
import type { ClickSource } from './constants';

/**
 * Dispatches the bank-statement extraction trigger for a given org.
 *
 * Used by both the dashboard banner ("Extract Now" link) and the empty-state
 * CTA on the Banking tab. Centralises the dispatch contract so the two call
 * sites stay in sync — particularly the orgId guard, which prevents firing
 * the action before the org is resolved from the route.
 *
 * @param orgId - The current org id, or undefined while it's still being
 *   resolved (e.g. on first paint before useParams settles). Calls become
 *   no-ops while undefined.
 * @returns A stable callback `(clickSource) => void` for the consumer.
 */
export const useExtractBankStatements = (orgId: string | undefined) => {
    const dispatch = useAppDispatch();
    return useCallback(
        (clickSource: ClickSource) => {
            if (!orgId) return;
            dispatch(triggerExtractPending({ orgId, clickSource }));
        },
        [dispatch, orgId],
    );
};
